import type { FC, ReactElement } from 'react';

import { CurrencyFormatter } from '../../formatters/CurrencyFormatter';
import { L10nText } from '../../services/uiTranslations/reactBindings/L10nText';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { useModifiers } from '../../styles/hooks/useModifiers';
import { formatNumber } from '../../utils/formatters';

import styles from './MaxPayoutText.module.css';

interface MaxPayoutTextProps {
  showAsterisk?: boolean;
  isHidden?: boolean;
  tk?: string;
  dynamicValues?: string[];
  isBetLimits?: boolean;
}

// TODO check if it comes from config/backend
const MAX_MULTIPLIER = 20_000;
const DEFAULT_MAX_PAYOUT_VALUE_IN_EUR = 500_000;
const DEFAULT_TK = 'MAXIMUM_PAYOUT_V3';

export const MaxPayoutText: FC<MaxPayoutTextProps> = ({
  isHidden = false,
  showAsterisk = false,
  tk = DEFAULT_TK,
  dynamicValues,
  isBetLimits,
}): ReactElement | null => {
  const locale = useSettingsSlice.currentLanguage();
  const isRTL = useSettingsSlice.getIsCurrentLanguageRTL();
  const config = useConfigSlice.tableConfig();

  const mods = useModifiers(styles, 'payout', { betLimits: isBetLimits }, { withBaseClass: true });

  if (isHidden) return null;

  const maxPayoutValue = config?.euroTablePayoutMax ?? DEFAULT_MAX_PAYOUT_VALUE_IN_EUR;
  const formattedMaxPayout = CurrencyFormatter.format(maxPayoutValue, locale, {
    currency: 'EUR',
    avoidTrailingZero: true,
  });
  const formattedMultiplier = formatNumber(locale, MAX_MULTIPLIER);
  const _dynamicValues = tk === DEFAULT_TK ? [formattedMultiplier, formattedMaxPayout] : dynamicValues;

  return (
    <div className={mods} dir={isRTL ? 'rtl' : 'ltr'}>
      {showAsterisk ? '* ' : null}
      <L10nText tk={tk} dynamicValues={_dynamicValues} />
    </div>
  );
};
